import { useQuery } from "@tanstack/react-query";
import { QUERY_KEYS } from "@/constants/QUERY_KEYS";
import type { NurseResponse } from "@vTypes/nurse-type";
import type * as type from "@vTypes";
import { useMemo } from "react";
import { useWantedStatusQuery } from "@/hooks/roster-craete/useWantedStatusQuery";
import { useWantedConfigQuery } from "@/hooks/roster-wanted/useWantedConfigQuery";
import httpClient from "@/config/axios";
import useRosterConfig from "@/hooks/useRosterConfig";
import { useShiftsQuery } from "@/hooks/roster-craete/useShiftsQuery";
import { useHolidayDatesQuery } from "@/hooks/useHolidayDatesQuery";
import { useShiftLookupMaps } from "@/hooks/shift/useShiftLookupMaps";

interface MyWeeklyOffResponse {
  my_weekly_off_weekday: number | null;
}

/** GET `/preferences/latest` 본문 */
export interface PreferencesLatestResponse {
  preference_data: type.PreferenceData | null;
  is_submitted: boolean;
  created_at: string;
  submitted_at: string | null;
}

// 간호사 목록 조회
export const useNurseListQuery = (groupId?: string) => {
  return useQuery<NurseResponse[]>({
    queryKey: QUERY_KEYS.nurses.list(groupId),
    queryFn: async () => {
      const params: { group_id?: string } = {};
      if (groupId) params.group_id = groupId;
      return await httpClient.get<NurseResponse[]>(`/nurses`, { params });
    },
    staleTime: 5 * 60 * 1000,
  });
};

// 원티드 목록 조회 (전체)
export const useWantedListQuery = (groupId?: string) => {
  return useQuery({
    queryKey: QUERY_KEYS.wanted.all(groupId),
    queryFn: async () => {
      const params: { group_id?: string } = {};
      if (groupId && groupId.trim() !== "" && groupId !== "null") {
        params.group_id = groupId;
      }
      return await httpClient.get<type.WantedAll[]>(`/wanted/all`, { params });
    },
    staleTime: 5 * 60 * 1000,
  });
};

// 내 주휴 정보 조회
export const useMyWeeklyOffQuery = (
  year?: number,
  month?: number,
  nurseId?: string
) => {
  return useQuery({
    queryKey: QUERY_KEYS.weeklyOff.my(year ?? 0, month ?? 0, nurseId),
    queryFn: async () => {
      const params: { year?: number; month?: number; nurse_id?: string } = {
        year,
        month,
      };
      if (nurseId) params.nurse_id = nurseId;
      return await httpClient.get<MyWeeklyOffResponse>(`/weekly-off/my`, {
        params,
      });
    },
    enabled: !!year && !!month,
  });
};

// 최신 선호도(원티드) 조회
export const usePreferencesLatestQuery = (
  year?: number,
  month?: number,
  groupId?: string,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: QUERY_KEYS.preferences.latest(year ?? 0, month ?? 0, groupId),
    queryFn: async () => {
      const params: { year?: number; month?: number; group_id?: string } = {
        year,
        month,
      };
      if (groupId && groupId.trim() !== "" && groupId !== "null") {
        params.group_id = groupId;
      }
      return await httpClient.get<PreferencesLatestResponse>(
        `/preferences/latest`,
        { params }
      );
    },
    enabled: !!year && !!month && enabled,
  });
};

// 전체 선호도 조회
export const usePreferencesAllQuery = (
  year?: number,
  month?: number,
  groupId?: string,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: QUERY_KEYS.preferences.all(year ?? 0, month ?? 0, groupId),
    queryFn: async () => {
      const params: { year?: number; month?: number; group_id?: string } = {
        year,
        month,
      };
      if (groupId && groupId.trim() !== "" && groupId !== "null") {
        params.group_id = groupId;
      }
      return await httpClient.get<type.PreferencesAll[]>(`/preferences/all`, {
        params,
      });
    },
    enabled: !!year && !!month && enabled,
  });
};

export const useRosterWantedResources = ({
  year,
  month,
  groupId,
  nurseId,
  isHeadNurse = false,
}: {
  year?: number;
  month?: number;
  groupId?: string;
  nurseId?: string;
  isHeadNurse?: boolean;
}) => {
  const {
    data: nurseList,
    isLoading: isNurseListLoading,
    error: nurseListError,
  } = useNurseListQuery(groupId);
  const {
    data: wantedList,
    isLoading: isWantedListLoading,
    error: wantedListError,
  } = useWantedListQuery(groupId);
  const {
    data: shiftsData,
    isLoading: isShiftsLoading,
    error: shiftsError,
  } = useShiftsQuery(groupId);

  const rosterWantedAvailableShifts = useMemo(
    () => (shiftsData ?? []) as unknown as type.availableShifts[],
    [shiftsData]
  );

  const { lookupMaps: shiftLookupMaps } = useShiftLookupMaps(
    rosterWantedAvailableShifts,
    shiftsData ?? null
  );

  const {
    data: holidayDatesData,
    isLoading: isHolidaysLoading,
    error: holidayError,
  } = useHolidayDatesQuery(year, month);

  const {
    data: myWeeklyOffData,
    isLoading: isMyWeeklyOffLoading,
    error: myWeeklyOffError,
  } = useMyWeeklyOffQuery(year, month);

  const {
    data: wantedConfig,
    isLoading: isWantedConfigLoading,
    error: wantedConfigError,
  } = useWantedConfigQuery({
    year,
    month,
    groupId: groupId ?? null,
    enabled: !!year && !!month,
  });

  const {
    data: wantedStatus,
    isLoading: isWantedStatusLoading,
    error: wantedStatusError,
  } = useWantedStatusQuery({
    year,
    month,
    groupId: groupId ?? "",
    enabled: isHeadNurse && !!year && !!month,
  });

  const {
    data: serverConfig,
    isLoading: isServerConfigLoading,
    error: serverConfigError,
  } = useRosterConfig();

  const currentWanted = useMemo(() => {
    if (!wantedList || !Array.isArray(wantedList) || !year || !month)
      return null;
    return wantedList.find(
      (w: any) =>
        Number(w.year) === Number(year) && Number(w.month) === Number(month)
    );
  }, [wantedList, year, month]);

  const isCurrentWantedClosed = currentWanted?.status === "closed";

  const {
    data: preferencesLatestData,
    isLoading: isPreferencesLatestLoading,
    error: preferencesLatestError,
  } = usePreferencesLatestQuery(year, month, groupId, !isCurrentWantedClosed);

  const {
    data: preferencesAllData,
    isLoading: isPreferencesAllLoading,
    error: preferencesAllError,
  } = usePreferencesAllQuery(
    year,
    month,
    groupId,
    (nurseList?.length ?? 0) > 0
  );

  const isLoading =
    isNurseListLoading ||
    isWantedListLoading ||
    isShiftsLoading ||
    (!!year &&
      !!month &&
      (isHolidaysLoading ||
        isMyWeeklyOffLoading ||
        isWantedConfigLoading ||
        isPreferencesLatestLoading ||
        isPreferencesAllLoading ||
        (isHeadNurse && isWantedStatusLoading) ||
        isServerConfigLoading));

  const error =
    nurseListError ||
    wantedListError ||
    shiftsError ||
    holidayError ||
    myWeeklyOffError ||
    wantedConfigError ||
    wantedStatusError ||
    preferencesLatestError ||
    preferencesAllError ||
    serverConfigError;

  return useMemo(() => {
    return {
      data: {
        nurseList,
        wantedList,
        shiftsData,
        shiftLookupMaps,
        holidayDatesData,
        myWeeklyOffData,
        wantedConfig,
        wantedStatus,
        preferencesLatestData,
        preferencesAllData,
        currentWanted,
        isCurrentWantedClosed,
        serverConfig,
      },
      isLoading,
      error,
    };
  }, [
    nurseList,
    wantedList,
    shiftsData,
    shiftLookupMaps,
    holidayDatesData,
    myWeeklyOffData,
    wantedConfig,
    wantedStatus,
    preferencesLatestData,
    preferencesAllData,
    currentWanted,
    isCurrentWantedClosed,
    isLoading,
    error,
    serverConfig,
  ]);
};
